import { Fragment, useState } from 'react';
import { useApp } from '../../lib/app';
import { supabase } from '../../lib/supabase';
import { useRows } from '../../lib/useRows';
import { undo } from '../../lib/api';
import { fmtDateTime } from '../../lib/format';
import { Badge, Button, Card, Empty, errText, useToast } from '../../ui/kit';

type A = {
  id: number; at: string; user_id: string | null; device: string | null; batch_id: string | null; table_name: string; row_id: string | null;
  site_id: string | null; action: string; before: Record<string, unknown> | null; after: Record<string, unknown> | null; summary: string | null;
  undo_of: number | null; undone_by: number | null;
};

const SKIP = new Set(['updated_at', 'updated_by', 'created_at', 'created_by']);
const TABLES = ['', 'sites', 'projects', 'buildings', 'contracts', 'organisations', 'contacts', 'site_contacts', 'files', 'profiles', 'user_roles', 'role_grants', 'roles'];

function changes(a: A): { key: string; from: unknown; to: unknown }[] {
  const keys = new Set([...Object.keys(a.before ?? {}), ...Object.keys(a.after ?? {})]);
  const out: { key: string; from: unknown; to: unknown }[] = [];
  for (const k of keys) {
    if (SKIP.has(k)) continue;
    const f = a.before?.[k] ?? null;
    const t = a.after?.[k] ?? null;
    if (JSON.stringify(f) !== JSON.stringify(t)) out.push({ key: k, from: f, to: t });
  }
  return out;
}

const show = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));

function toCsv(rows: A[], who: (id: string | null) => string): string {
  const esc = (s: string) => `"${s.replace(/"/g, '""')}"`;
  const head = ['id', 'when', 'who', 'action', 'table', 'record', 'summary', 'changes', 'batch'];
  const lines = rows.map((a) =>
    [String(a.id), a.at, who(a.user_id), a.action, a.table_name, a.row_id ?? '', a.summary ?? '',
      changes(a).map((c) => `${c.key}: ${show(c.from)} → ${show(c.to)}`).join('; '), a.batch_id ?? ''].map(esc).join(','),
  );
  return [head.join(','), ...lines].join('\n');
}

export default function Audit() {
  const { access, siteId } = useApp();
  const toast = useToast();
  const isSuper = Boolean(access?.super_admin);
  const [table, setTable] = useState('');
  const [person, setPerson] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [thisSite, setThisSite] = useState(false);
  const [open, setOpen] = useState<number | null>(null);

  const people = useRows<{ id: string; full_name: string | null; email: string | null }>(() => supabase.from('profiles').select('id, full_name, email'), []);
  const log = useRows<A>(() => {
    let qy = supabase.from('audit_log').select('*').order('id', { ascending: false }).limit(300);
    if (table) qy = qy.eq('table_name', table);
    if (person) qy = qy.eq('user_id', person);
    if (from) qy = qy.gte('at', from + 'T00:00:00+12:00');
    if (to) qy = qy.lte('at', to + 'T23:59:59+12:00');
    if (thisSite && siteId) qy = qy.eq('site_id', siteId);
    return qy;
  }, [table, person, from, to, thisSite, siteId], ['audit_log']);

  const who = (id: string | null) => {
    if (!id) return 'System';
    const p = people.rows.find((x) => x.id === id);
    return p?.full_name || p?.email || id.slice(0, 8);
  };

  async function doUndo(a: A, batch: boolean, force = false) {
    const label = batch ? 'every change in this batch' : `change #${a.id}`;
    if (!force && !window.confirm(`Undo ${label}?`)) return;
    try {
      const r = await undo(batch ? { batch_id: a.batch_id!, force } : { audit_id: a.id, force });
      if (!r.ok && r.conflicts?.length) {
        if (window.confirm(`The record has changed since (${r.conflicts.length} later change${r.conflicts.length > 1 ? 's' : ''}). Undo anyway and overwrite them?`)) {
          return doUndo(a, batch, true);
        }
        return;
      }
      toast({ tone: 'ok', text: `Undone (${r.undone ?? 0})` });
      void log.reload();
    } catch (e) {
      toast({ tone: 'error', text: errText(e) });
    }
  }

  function exportCsv() {
    const blob = new Blob([toCsv(log.rows, who)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `pmc3-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <Card title="Every change, who made it and when" actions={<Button onClick={exportCsv}>Export CSV</Button>} pad={false}>
      <div className="toolbar" style={{ padding: '12px 16px' }}>
        <select value={table} onChange={(e) => setTable(e.target.value)} aria-label="Table">
          {TABLES.map((t) => (
            <option key={t} value={t}>
              {t || 'All records'}
            </option>
          ))}
        </select>
        <select value={person} onChange={(e) => setPerson(e.target.value)} aria-label="Person">
          <option value="">Everyone</option>
          {people.rows.map((p) => (
            <option key={p.id} value={p.id}>
              {p.full_name || p.email}
            </option>
          ))}
        </select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
        <label className="check small">
          <input type="checkbox" checked={thisSite} onChange={(e) => setThisSite(e.target.checked)} /> This site only
        </label>
      </div>
      {log.loading ? (
        <Empty>Loading…</Empty>
      ) : log.rows.length === 0 ? (
        <Empty>No changes match.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="t">
            <thead>
              <tr>
                <th>#</th>
                <th>When</th>
                <th>Who</th>
                <th>What</th>
                <th>Record</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {log.rows.map((a) => {
                const ch = changes(a);
                const name = String(a.after?.name ?? a.after?.title ?? a.after?.full_name ?? a.after?.original_name ?? a.before?.name ?? a.row_id ?? '');
                return (
                  <Fragment key={a.id}>
                    <tr className="click" onClick={() => setOpen(open === a.id ? null : a.id)}>
                      <td className="muted small">{a.id}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(a.at)}</td>
                      <td>{who(a.user_id)}</td>
                      <td>
                        <Badge tone={a.action === 'delete' ? 'warn' : a.action === 'undo' ? 'dark' : a.action === 'insert' ? 'good' : 'neutral'}>{a.action}</Badge>{' '}
                        <span className="small">{a.summary ?? ''}</span>
                        {a.undone_by ? <div className="small muted">undone by #{a.undone_by}</div> : null}
                      </td>
                      <td>
                        <span className="muted small">{a.table_name}</span>
                        <div>{name}</div>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                        {isSuper && !a.undone_by && a.action !== 'undo' && a.action !== 'hard_delete' ? (
                          <>
                            <Button kind="ghost" onClick={() => void doUndo(a, false)}>
                              Undo
                            </Button>
                            {a.batch_id && log.rows.filter((x) => x.batch_id === a.batch_id).length > 1 ? (
                              <Button kind="ghost" onClick={() => void doUndo(a, true)}>
                                Undo batch
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </td>
                    </tr>
                    {open === a.id ? (
                      <tr>
                        <td colSpan={6} style={{ background: '#faf7f2' }}>
                          {ch.length === 0 ? (
                            <span className="muted small">No field changes recorded.</span>
                          ) : (
                            <table className="t" style={{ background: '#fff' }}>
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Before</th>
                                  <th>After</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ch.map((c) => (
                                  <tr key={c.key}>
                                    <td>{c.key}</td>
                                    <td className="small">{show(c.from)}</td>
                                    <td className="small">{show(c.to)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          <div className="muted small" style={{ marginTop: 6 }}>
                            Device: {a.device ?? '—'} · batch {a.batch_id ?? '—'}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
